import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col } from 'react-bootstrap';
import Pagination from 'react-bootstrap/Pagination';
import './Paging.css';

const range = (start, end) => {
    const values = [];
    for (let value = start; value <= end; value += 1) {
        values.push(value);
    }
    return values;
};

const buildPaginationRange = (currentPage, totalPages) => {
    const siblingCount = 1;
    const totalPageNumbers = siblingCount * 2 + 5;

    if (totalPages <= totalPageNumbers) {
        return range(1, totalPages);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 2);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages - 1);
    const showLeftEllipsis = leftSiblingIndex > 2;
    const showRightEllipsis = rightSiblingIndex < totalPages - 1;

    const paginationRange = [1];

    if (!showLeftEllipsis) {
        paginationRange.push(...range(2, leftSiblingIndex - 1));
    } else {
        paginationRange.push('start-ellipsis');
    }

    paginationRange.push(...range(leftSiblingIndex, rightSiblingIndex));

    if (!showRightEllipsis) {
        paginationRange.push(...range(rightSiblingIndex + 1, totalPages - 1));
    } else {
        paginationRange.push('end-ellipsis');
    }

    paginationRange.push(totalPages);

    return paginationRange;
};

/**
 * Paging component handles pagination controls and display.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.title - Title displayed at the top of the component.
 * @param {ReactNode} props.content - The content or data to display.
 * @param {number} props.pageSize - Number of items per page.
 * @param {number} props.pageNumber - Current page number.
 * @param {number} props.totalResults - Total number of results available.
 * @param {number} props.numberOfPages - Total number of pages.
 * @param {Function} props.onChange - Callback to change the page.
 * @param {Function} props.pageSizeChange - Callback when page size changes.
 * @param {Function} props.pageSizeSet - Callback to set the page size.
 *
 * @returns {JSX.Element} The rendered pagination component.
 */
export const Paging = (props) => {
    const {
        title,
        content,
        pageSize: pageSizeProp,
        pageNumber,
        totalResults,
        numberOfPages,
        onChange,
        pageSizeChange,
        pageSizeSet,
    } = props;

    const [pageSize, setPageSize] = useState(pageSizeProp);
    useEffect(() => {
        setPageSize(pageSizeProp);
    }, [pageSizeProp]);

    const currentPage = pageNumber || 1;
    const totalPages = Math.max(
        numberOfPages || Math.ceil((totalResults || 0) / (pageSize || 1)),
        1
    );

    const goToPage = useCallback(
        (page) => {
            if (page === currentPage || page < 1 || page > totalPages) {
                return;
            }
            onChange(page);
        },
        [currentPage, totalPages, onChange]
    );

    const paginationRange = useMemo(
        () => buildPaginationRange(currentPage, totalPages),
        [currentPage, totalPages]
    );

    /**
     * Handles the change in items per page.
     *
     * @param {React.ChangeEvent<HTMLSelectElement>} e - The change event.
     */
    const onChangePerPage = (e) => {
        const newPageSize = parseFloat(e.target.value);
        pageSizeChange(newPageSize);
        pageSizeSet(newPageSize);
        setPageSize(newPageSize);
    };

    const shouldShowPagination = totalResults > 10;

    return (
        <Col md={12} lg={9}>
            <div className="content">
                <h2>{title}</h2>
                {content}

                {shouldShowPagination && (
                    <>
                        <div className="pagination-custom">
                            <Pagination className="mb-0">
                                <Pagination.First
                                    disabled={currentPage <= 1}
                                    onClick={() => goToPage(1)}
                                />
                                <Pagination.Prev
                                    disabled={currentPage <= 1}
                                    onClick={() => goToPage(currentPage - 1)}
                                />
                                {paginationRange.map((item) => {
                                    if (item === 'start-ellipsis' || item === 'end-ellipsis') {
                                        return <Pagination.Ellipsis key={item} disabled />;
                                    }

                                    return (
                                        <Pagination.Item
                                            key={item}
                                            active={item === currentPage}
                                            onClick={() => goToPage(item)}
                                        >
                                            {item}
                                        </Pagination.Item>
                                    );
                                })}
                                <Pagination.Next
                                    disabled={currentPage >= totalPages}
                                    onClick={() => goToPage(currentPage + 1)}
                                />
                                <Pagination.Last
                                    disabled={currentPage >= totalPages}
                                    onClick={() => goToPage(totalPages)}
                                />
                            </Pagination>
                        </div>
                        <div className="pagination-custom">
                            <label htmlFor="table-page-size">Items per Page:&nbsp;</label>
                            <select
                                value={pageSize}
                                onChange={onChangePerPage}
                                id="table-page-size"
                                name="table-page-size"
                            >
                                <option value="10">10</option>
                                <option value="15">15</option>
                                <option value="30">30</option>
                                <option value="50">50</option>
                            </select>
                        </div>
                        <div className="pagination-custom">
                            <label>Total pages: {totalPages}</label>
                        </div>
                    </>
                )}
            </div>
        </Col>
    );
};
